/**
 * StorageSandbox implementation — transparent namespace wrapper for localStorage and sessionStorage.
 *
 * Design: .planning/phases/151-sandbox-architecture-design/STORAGE-SANDBOX-DESIGN.md
 * Interface: ./storage-sandbox.types.ts
 *
 * Key principle: clear() NEVER calls backingStore.clear().
 * Uses two-pass (collect then delete) to avoid localStorage index-shift bug.
 * Studio keys (cogo-story-*, cogo-studio-*, etc.) are always preserved.
 */

import type {
  SandboxStorage,
  StorageSandbox,
  StorageSandboxOptions,
  CreateStorageSandbox,
} from './storage-sandbox.types.js'

/**
 * Creates a SandboxStorage — a namespaced wrapper around a real Storage object.
 *
 * All key operations transparently add/remove the namespace prefix.
 * The app code uses original key names; the backing store sees prefixed keys.
 *
 * @param prefix - Namespace prefix, e.g. 'app:commerce-001:' or 'sess:commerce-001:'
 * @param backingStore - The real Storage object (localStorage or sessionStorage)
 */
function createSandboxStorage(prefix: string, backingStore: Storage): SandboxStorage {
  return {
    /**
     * Returns value for originalKey in this namespace.
     * Reads from backingStore using prefixed key.
     */
    getItem(originalKey: string): string | null {
      return backingStore.getItem(prefix + originalKey)
    },

    /**
     * Sets value for originalKey in this namespace.
     * Writes to backingStore using prefixed key.
     */
    setItem(originalKey: string, value: string): void {
      backingStore.setItem(prefix + originalKey, value)
    },

    /**
     * Removes originalKey from this namespace.
     * Removes prefixed key from backingStore.
     */
    removeItem(originalKey: string): void {
      backingStore.removeItem(prefix + originalKey)
    },

    /**
     * Clears ALL keys in this namespace using two-pass algorithm.
     *
     * NEVER calls backingStore.clear() — Studio keys are preserved.
     *
     * Two-pass rationale: localStorage.key(i) uses numeric indices.
     * When a key is removed, all higher-index keys shift down by 1.
     * Deleting during iteration skips every other key.
     * Solution: collect all matching keys first, then delete them.
     */
    clear(): void {
      const toDelete: string[] = []

      // Pass 1: Collect all matching keys (read-only iteration)
      for (let i = 0; i < backingStore.length; i++) {
        const k = backingStore.key(i)
        if (k !== null && k.startsWith(prefix)) {
          toDelete.push(k)
        }
      }

      // Pass 2: Delete collected keys (no index shifts since not iterating)
      toDelete.forEach((k) => backingStore.removeItem(k))

      // NEVER calls backingStore.clear()
    },

    /**
     * Returns count of keys in this namespace only.
     * Iterates backingStore and counts keys starting with prefix.
     */
    get length(): number {
      let count = 0
      for (let i = 0; i < backingStore.length; i++) {
        const k = backingStore.key(i)
        if (k !== null && k.startsWith(prefix)) {
          count++
        }
      }
      return count
    },

    /**
     * Returns the nth key in this namespace (without prefix) or null.
     * The returned value is the original key name as the app code knows it.
     */
    key(n: number): string | null {
      let found = 0
      for (let i = 0; i < backingStore.length; i++) {
        const k = backingStore.key(i)
        if (k !== null && k.startsWith(prefix)) {
          if (found === n) {
            return k.slice(prefix.length) // strip prefix — return original key
          }
          found++
        }
      }
      return null
    },

    /**
     * The namespace prefix for this storage instance.
     * e.g. 'app:commerce-001:' or 'sess:commerce-001:'.
     */
    get prefix(): string {
      return prefix
    },

    /**
     * Returns all original key names in this namespace (without prefix).
     * Equivalent to iterating key(0) through key(length - 1).
     */
    keys(): string[] {
      const result: string[] = []
      for (let i = 0; i < backingStore.length; i++) {
        const k = backingStore.key(i)
        if (k !== null && k.startsWith(prefix)) {
          result.push(k.slice(prefix.length))
        }
      }
      return result
    },
  }
}

/**
 * Creates a new StorageSandbox instance that namespaces both localStorage and sessionStorage.
 *
 * The sandbox wraps each storage with a prefix:
 *   - localStorage:   'app:{appId}:{originalKey}'
 *   - sessionStorage: 'sess:{appId}:{originalKey}'
 *
 * App code uses original key names. Studio code uses window.localStorage directly (no change needed).
 */
export const createStorageSandbox: CreateStorageSandbox = (
  options: StorageSandboxOptions
): StorageSandbox => {
  const { appId } = options

  // Resolve backing stores — allow injection for testing; fall back to globals
  const localBackingStore: Storage =
    options.localStore ??
    (typeof window !== 'undefined' ? window.localStorage : ({} as Storage))
  const sessionBackingStore: Storage =
    options.sessionStore ??
    (typeof window !== 'undefined' ? window.sessionStorage : ({} as Storage))

  const localPrefix = `app:${appId}:`
  const sessionPrefix = `sess:${appId}:`

  let local: SandboxStorage | null = createSandboxStorage(localPrefix, localBackingStore)
  let session: SandboxStorage | null = createSandboxStorage(sessionPrefix, sessionBackingStore)

  return {
    get local(): SandboxStorage {
      // After destroy, local is null. Return a no-op wrapper to avoid crashes.
      // In practice, BlockV2 should not call sandbox methods after destroy().
      return local!
    },
    get session(): SandboxStorage {
      return session!
    },
    get appId(): string {
      return appId
    },
    /**
     * Destroy sandbox instance — releases references to wrapper objects.
     * Does NOT delete stored data. 'app:{appId}:*' keys remain in localStorage
     * for the duration of the browser session.
     */
    destroy(): void {
      // Nullify references (allow GC) — do NOT delete stored data
      local = null
      session = null
    },
  }
}
